import { SpringValue } from '@react-spring/web';
import React from 'react';

import { keyframes } from '@tanium/react-emotion-9';

import { animationDurationMs } from '../../utils/animationDurationMs';
import { ColorScale } from '../../utils/ColorScale';
import { cubicBezierCurve } from '../../utils/cubicBezierCurve';
import { DataKeyType } from '../../utils/DataProps';
import { TimeScale } from '../../utils/TimeScale';
import { CreatePortalFn } from '../../utils/usePortal';
import { AnimationSvgWrapper } from '../utils/AnimationSvgWrapper';
import { Circle } from './Circle';
import { XYPoint } from './XYPoint';

const DEFAULT_POINT_RADIUS = 4;

const getInitialTransform = (fromYValue: number) => `translateY(${fromYValue}px)`;

const getAnimation = (fromYValue: number) => {
  const grow = keyframes({
    '0%': {
      transform: getInitialTransform(fromYValue),
    },
    '100%': {
      transform: 'translateY(0px)',
    },
  });

  return `${grow} ${animationDurationMs}ms ${cubicBezierCurve} both`;
};

interface PointProps<TDataKeyType extends DataKeyType> {
  cx: SpringValue<number | string>;
  cy: SpringValue<number | string>;
  opacity: SpringValue<number | string>;
  item: XYPoint<TDataKeyType>;
  index: number;
  colorScale: ColorScale<TDataKeyType>;
  xScale: TimeScale;
  isActivePoint: boolean;
  isInitialAnimation: boolean;
  plotBottom: number;
  isLastSeries: boolean;
  createPortal: CreatePortalFn;
  onInitialAnimationEnd?: React.AnimationEventHandler;
}

interface PointComponent {
  <TDataKeyType extends DataKeyType>(props: PointProps<TDataKeyType>): JSX.Element;
}

export const Point = React.memo(
  <TDataKeyType extends DataKeyType>({
    colorScale,
    item,
    index: i,
    xScale,
    isActivePoint,
    isInitialAnimation,
    plotBottom,
    isLastSeries,
    createPortal,
    onInitialAnimationEnd,
    ...props
  }: PointProps<TDataKeyType>) => {
    const { key: seriesDataKey, xValue, pointScaledValue } = item;

    const color = colorScale.get(seriesDataKey);

    const scaledX = xScale(xValue);
    const scaledY = pointScaledValue;

    if (scaledX === undefined || scaledY === undefined) {
      return null;
    }

    const point = <Circle {...props} fill={color} r={isActivePoint ? 7 : DEFAULT_POINT_RADIUS} />;

    const handleOnAnimationEnd: React.AnimationEventHandler | undefined = isLastSeries
      ? onInitialAnimationEnd
      : undefined;

    const animateFromYValue = plotBottom - scaledY;

    const initialPointRender =
      isInitialAnimation &&
      createPortal(
        <AnimationSvgWrapper
          animation={getAnimation(animateFromYValue)}
          transform={getInitialTransform(animateFromYValue)}
          onAnimationEnd={handleOnAnimationEnd}
        >
          <Circle {...props} fill={color} r={DEFAULT_POINT_RADIUS} />;
        </AnimationSvgWrapper>,
      );

    return (
      <>
        <g opacity={isInitialAnimation ? 0 : undefined}>{point}</g>
        {initialPointRender}
      </>
    );
  },
) as PointComponent;
